export class AuthError extends Error {
  readonly status: 401 | 403

  constructor(message: string, status: 401 | 403 = 401) {
    super(message)
    this.name = 'AuthError'
    this.status = status
  }
}

/**
 * Cloudflare Access JWT verification, performed by the ORIGIN.
 *
 * The Worker validates too, but the origin must not trust a header the Worker
 * supplies: if it did, anyone who reached the origin could assert any identity.
 * Both `iss` and `aud` are pinned — signature validity alone is not the security
 * property, because a token minted for a different Access application is
 * perfectly well signed.
 */

export interface AccessConfig {
  /** e.g. https://myteam.cloudflareaccess.com */
  readonly teamDomain: string
  /** The Access application's AUD tag. */
  readonly aud: string
  /** Clock skew allowance in seconds. */
  readonly skewSeconds?: number
}

interface Jwk {
  kid: string
  kty: string
  n: string
  e: string
  alg?: string
}

export function b64urlToBytes(value: string): Uint8Array<ArrayBuffer> {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/')
  const binary = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4))
  const bytes = new Uint8Array(new ArrayBuffer(binary.length))
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes
}

function decodeJson(segment: string): any {
  return JSON.parse(new TextDecoder().decode(b64urlToBytes(segment)))
}

export interface AccessIdentity {
  readonly email: string
  readonly sub: string
  readonly expiresAt: number
}

export class AccessVerifier {
  private keys: Map<string, CryptoKey> | null = null
  private fetchedAt = 0

  /**
   * The load that is currently on its way, shared by everyone waiting on it.
   *
   * WITHOUT THIS, ONE ROTATION IS A STAMPEDE. Every request that arrives while
   * the certs endpoint is being read would open its own connection — a busy
   * mailbox at the moment the TTL lapses, or a burst of tokens carrying a kid we
   * have never seen, becomes as many requests to Cloudflare as there are
   * in-flight requests to us. Held here so the answer is fetched once and handed
   * to all of them.
   */
  private inflight: Promise<Map<string, CryptoKey>> | null = null

  /**
   * Bumped on every successful load, so `verify` can tell "the set I looked in
   * is stale" from "the set I looked in was fetched a microsecond ago, by me".
   * Without the distinction a cold start holding a bogus token fetches twice for
   * one request, and so does a caller whose neighbour already refetched.
   */
  private generation = 0

  /**
   * When a KID MISS last made us fetch. The TTL path does not touch this.
   *
   * The 60 s default is the whole trade, stated once: a rotation now costs at
   * most a minute of 401s per isolate instead of a TTL's worth of them, and a
   * flood of forged tokens costs Cloudflare one certs request a minute rather
   * than one per token.
   */
  private forcedAt = 0

  private readonly config: AccessConfig
  private readonly fetchImpl: typeof fetch
  private readonly ttlSeconds: number
  private readonly forcedIntervalSeconds: number

  /**
   * THE WRAPPER AROUND `fetchImpl` IS LOAD-BEARING, and leaving it out took the
   * whole client down the first time a browser used it.
   *
   * `fetch` is a native function that requires its own global `this`. Stored on
   * an instance and invoked as `this.fetchImpl(...)`, it is called with the
   * AccessVerifier as its receiver, and both the Workers runtime and undici
   * refuse in the same way:
   *
   *     Illegal invocation: function called with incorrect `this` reference.
   *
   * Re-wrapping here calls whatever was passed DETACHED — `this` is undefined,
   * which is what a native global expects — and it covers an injected
   * implementation as well as the default, so a caller doing
   * `new AccessVerifier(config, fetch)` cannot reintroduce it.
   *
   * WHY NO TEST CAUGHT IT: every test injected a plain function double, and a
   * plain function does not care what its receiver is. The default path — the
   * only one production uses — was never executed. `access.test.ts` now runs it
   * against a stub that enforces the native contract.
   *
   * `sync/hint.ts:88` has the same shape and is fine, because it holds the
   * function in a LOCAL const and calls that: `doFetch(url)` is already
   * detached. The difference is one keyword and it is the whole bug.
   */
  constructor(config: AccessConfig, fetchImpl: typeof fetch = fetch, ttlSeconds = 3600, forcedIntervalSeconds = 60) {
    this.config = config
    this.fetchImpl = (...args) => fetchImpl(...args)
    this.ttlSeconds = ttlSeconds
    this.forcedIntervalSeconds = forcedIntervalSeconds
  }

  private get certsUrl(): string {
    return `${this.config.teamDomain.replace(/\/$/, '')}/cdn-cgi/access/certs`
  }

  /** The cached set while it is fresh; otherwise whatever load is going on. */
  private async keySet(now: number): Promise<Map<string, CryptoKey>> {
    if (this.keys && now - this.fetchedAt < this.ttlSeconds) return this.keys
    return this.load(now)
  }

  /**
   * A key set that has NOT already been consulted, or null when asking for one
   * would be pointless or rude.
   *
   * THIS IS THE ROTATION FIX. Cloudflare Access rotates its signing keys, and a
   * `kid` the cached set does not carry is exactly what that looks like from
   * here. On a TTL alone the answer is 401 — for every member of the household,
   * at BOTH ends (the Worker and the origin cache separately), for up to an hour,
   * with nothing in either log naming the cause. So a kid miss buys one refetch.
   *
   * It has to be rate-limited, because "unknown kid" is also what a stream of
   * forged tokens looks like, and an unthrottled refetch would turn that into a
   * denial-of-service aimed at Cloudflare on our behalf.
   */
  private async keysForUnknownKid(now: number, consulted: number): Promise<Map<string, CryptoKey> | null> {
    // The set moved under us: either this very call loaded it (a cold start
    // cannot be stale) or a concurrent request's refetch landed. Either way the
    // current set is the newest that exists — at worst the one already looked
    // in — and fetching again would answer the same thing.
    if (this.generation !== consulted) return this.keys
    // Someone is already asking. Wait for THAT answer rather than opening a
    // second connection or 401ing a caller who is one microtask from the key.
    if (this.inflight) return this.inflight
    if (now - this.forcedAt < this.forcedIntervalSeconds) return null
    // Stamped before the await, so the cooldown covers the ATTEMPT. A certs
    // endpoint that is down or slow must throttle the same as one that answers.
    this.forcedAt = now
    return this.load(now)
  }

  private load(now: number): Promise<Map<string, CryptoKey>> {
    this.inflight ??= this.fetchKeySet()
      .then((map) => {
        this.keys = map
        this.fetchedAt = now
        this.generation++
        return map
      })
      // Cleared on failure too: a certs endpoint that 502s once must not leave a
      // rejected promise latched here for every later caller to await.
      .finally(() => {
        this.inflight = null
      })
    return this.inflight
  }

  private async fetchKeySet(): Promise<Map<string, CryptoKey>> {
    const response = await this.fetchImpl(this.certsUrl)
    if (!response.ok) throw new AuthError(`Could not fetch Access keys: ${response.status}`)
    const body = (await response.json()) as { keys?: Jwk[] }
    const map = new Map<string, CryptoKey>()
    for (const jwk of body.keys ?? []) {
      if (jwk.kty !== 'RSA') continue
      const key = await crypto.subtle.importKey(
        'jwk',
        { kty: 'RSA', n: jwk.n, e: jwk.e, alg: 'RS256', ext: true },
        { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-256' },
        false,
        ['verify'],
      )
      map.set(jwk.kid, key)
    }
    if (map.size === 0) throw new AuthError('Access key set is empty')
    return map
  }

  async verify(token: string, now: number): Promise<AccessIdentity> {
    const parts = token.split('.')
    if (parts.length !== 3) throw new AuthError('Malformed Access token')
    const [headerSeg, payloadSeg, signatureSeg] = parts as [string, string, string]

    const header = decodeJson(headerSeg)
    // Pin the algorithm explicitly. Cloudflare's own sample omits this; without
    // it a token could arrive with a different alg than the one we verify under.
    if (header.alg !== 'RS256') throw new AuthError(`Unexpected JWT alg: ${header.alg}`)
    if (typeof header.kid !== 'string') throw new AuthError('Access token has no kid')

    // Read BEFORE the cache is consulted, so `keysForUnknownKid` can tell
    // whether the set it is being asked to replace is one this call just loaded.
    const consulted = this.generation
    const keys = await this.keySet(now)
    let key = keys.get(header.kid)
    if (!key) key = (await this.keysForUnknownKid(now, consulted))?.get(header.kid)
    if (!key) throw new AuthError('Unknown Access signing key')

    const valid = await crypto.subtle.verify(
      'RSASSA-PKCS1-v1_5',
      key,
      b64urlToBytes(signatureSeg),
      new TextEncoder().encode(`${headerSeg}.${payloadSeg}`),
    )
    if (!valid) throw new AuthError('Access token signature is invalid')

    const payload = decodeJson(payloadSeg)
    const skew = this.config.skewSeconds ?? 60

    const audiences: string[] = Array.isArray(payload.aud) ? payload.aud : [payload.aud]
    // Without this check any Access application in the account could mint a token
    // that passes signature verification here.
    if (!audiences.includes(this.config.aud)) throw new AuthError('Access token is for a different application', 403)
    if (payload.iss !== this.config.teamDomain.replace(/\/$/, '')) throw new AuthError('Access token issuer mismatch', 403)
    if (typeof payload.exp !== 'number' || payload.exp + skew < now) throw new AuthError('Access token has expired')
    if (typeof payload.nbf === 'number' && payload.nbf - skew > now) throw new AuthError('Access token is not yet valid')
    if (typeof payload.email !== 'string') throw new AuthError('Access token carries no email')

    return { email: payload.email.toLowerCase(), sub: String(payload.sub ?? ''), expiresAt: payload.exp }
  }
}
